import styled from '@emotion/styled';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { deleteAccount } from '../../../utils/api/user';
import { formatDate } from '../../../utils/helpers/date';
import { breakpoints, colors, spacing } from '../../../utils/styleguide';
import GhostButton from '../buttons/GhostButton';
import PrimaryButton from '../buttons/PrimaryButton';
import Icon from '../Icon';
import { Col, Container, Row } from '../layout/12ColGridLayout';
import AuthenticatedLayout from '../layout/AuthenticatedLayout';
import SimpleModal from '../modals/SimpleModal';
import Brush from '../text/Brush';
import Typography from '../text/Typography';
import AccountInfoSection from './AccountInfoSection';
import ErrorBox from '../ErrorBox';
import { useAuth } from '../../../utils/authContext';

const PageContainer = styled(Container)`
  min-height: 600px;
  text-align: left;
  ${breakpoints.tablet} {
    text-align: center;
  }
`;

const BadgeCol = styled(Col)`
  display: flex;
  margin: ${spacing[4]}px;
  gap: ${spacing[2]}px;
  text-align: center;
  justify-content: center;
`;

const Title = styled(Typography)`
  text-align: center;
  padding: ${spacing[3]}px 0 ${spacing[4]}px;
`;

const ButtonRow = styled(Row)`
  text-align: center;
`;

const LogoutButton = styled(PrimaryButton)`
  margin: ${spacing[3]}px 0;
`;

const DeleteAccountButton = styled(GhostButton)`
  margin: ${spacing[5]}px 0;
`;

const ModalBody = styled.div`
  flex-direction: column;
  display: flex;
  align-items: center;
  text-align: center;
`;

const ModalTitle = styled(Typography)`
  margin-top: ${spacing[3]}px;
`;

const UndoneCopy = styled(Typography)`
  margin: ${spacing[1]}px 0 ${spacing[3]}px;
`;

const CancelButton = styled(PrimaryButton)`
  margin: ${spacing[4]}px 0;
`;

const Account = () => {
  const router = useRouter();
  const { verifiedUser, clearUser } = useAuth();

  const [showDeletionErrorMessage, setShowDeletionErrorMessage] = useState(false);
  const [showDeleteAccountConfirmation, setShowDeleteAccountConfirmation] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  const memberSince = verifiedUser?.createdAt
    ? formatDate({ date: new Date(verifiedUser.createdAt), options: { dateStyle: 'long' } })
    : undefined;

  async function onDeleteAccount(userId: number) {
    setIsDeletingAccount(true);
    try {
      await deleteAccount(userId);
      clearUser();
      router.push('/logout');
    } catch (error) {
      setIsDeletingAccount(false);
      setShowDeletionErrorMessage(true);
    }
  }
  if (!verifiedUser) {
    return null;
  }
  return (
    <>
      <Head>
        <title>Your account - Milkywire</title>
      </Head>
      <PageContainer disableMaxWidth>
        <Row>
          <BadgeCol desktop="4 / span 6">
            {Object.keys(verifiedUser.categories || {})
              .concat(Object.keys(verifiedUser.roles || {}))
              .map((key) => (
                <Brush key={key}>
                  {(key == 'ADMIN' || key == 'IMPACTER' || key == 'MW_EMPLOYEE') && (
                    <Typography variant="h4">{key}</Typography>
                  )}
                </Brush>
              ))}
          </BadgeCol>
        </Row>
        <Row>
          <Col>
            <Title variant="h4">Your account</Title>
          </Col>
        </Row>
        <AccountInfoSection
          label="Full name"
          value={`${verifiedUser.firstName} ${verifiedUser.lastName}`}
        />
        <AccountInfoSection label="Email" value={verifiedUser.email} />
        {memberSince && <AccountInfoSection label="Member since" value={memberSince} />}
        <ButtonRow>
          <Col>
            <LogoutButton href={'/logout'} size="small" color="purple" label="Log out" />
          </Col>
        </ButtonRow>
        <ButtonRow>
          <Col>
            <DeleteAccountButton
              color={colors.redDark}
              onClick={() => setShowDeleteAccountConfirmation(true)}
              hideUnderline
            >
              Delete account
            </DeleteAccountButton>
          </Col>
        </ButtonRow>
        {showDeletionErrorMessage && (
          <ErrorBox
            message="Something went wrong. Please contact hello@milkywire.com for assistance."
            onClose={() => setShowDeletionErrorMessage(false)}
          />
        )}
      </PageContainer>
      <SimpleModal
        open={showDeleteAccountConfirmation || isDeletingAccount}
        centerContent
        body={
          <ModalBody>
            <Icon name="caution" size={80} />
            <ModalTitle variant="h4">Delete your account?</ModalTitle>
            <UndoneCopy variant="bodySmall">This can&apos;t be undone.</UndoneCopy>
            <Typography variant="bodySmall">Deleting your account will:</Typography>
            <Typography variant="bodySmall">· Remove all your data associated with it</Typography>
            <Typography variant="bodySmall">· Cancel any subscriptions and payments</Typography>
            <CancelButton
              color="purple"
              label={'Cancel'}
              onClick={() => setShowDeleteAccountConfirmation(false)}
              loading={isDeletingAccount}
            />
            <GhostButton
              label="Delete account"
              onClick={async () => {
                onDeleteAccount(verifiedUser.id);
                setShowDeleteAccountConfirmation(false);
              }}
              hideUnderline
              color={colors.redDark}
            />
          </ModalBody>
        }
      />
    </>
  );
};

function AccountPage() {
  return (
    <AuthenticatedLayout>
      <Account />
    </AuthenticatedLayout>
  );
}

export default AccountPage;
